import { Badge } from '@/components/ui/badge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import { FormSection, MasterDataForm } from '../components/master-data/MasterDataForm';
import { FilterConfig, MasterDataTable, TableColumn } from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { buildCsv } from '../lib/csv';
import { downloadText } from '../lib/download';
import { Dispatch } from '../types/dispatch';

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const DispatchManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingDispatch, setEditingDispatch] = useState<Dispatch | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<{ sortBy?: string; sortOrder?: 'asc' | 'desc' }>({});

  // Fetch dispatches with server-side filtering and pagination
  const { data: dispatchesResponse, isLoading } = useQuery({
    queryKey: ['dispatches', currentPage, pageSize, searchTerm, currentFilters, sortConfig],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });
      
      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(`/api/master-data/dispatch?${params.toString()}`);
      const result = response.data.data || {};
      
      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1
        }
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const dispatches = dispatchesResponse?.data || [];
  const pagination = dispatchesResponse?.pagination;

  // Create dispatch mutation
  const createDispatchMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/dispatch', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatches'] });
      toast({
        title: 'Dispatch Type created',
        description: 'Dispatch Type has been created successfully.',
      });
      setIsFormOpen(false);
      setEditingDispatch(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error',
          description: error.response?.data?.error?.message || 'Failed to create dispatch type',
          variant: 'destructive',
        });
      }
    },
  });

  // Update dispatch mutation
  const updateDispatchMutation = useMutation({
    mutationFn: async (data: any) => {
      const { _id, ...updateData } = data;
      const response = await axios.put(`/api/master-data/dispatch/${_id}`, updateData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatches'] });
      toast({
        title: 'Dispatch Type updated',
        description: 'Dispatch Type has been updated successfully.',
      });
      setIsFormOpen(false);
      setEditingDispatch(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error',
          description: error.response?.data?.error?.message || 'Failed to update dispatch type',
          variant: 'destructive',
        });
      }
    },
  });

  // Delete dispatch mutation (hard delete)
  const deleteDispatchMutation = useMutation({
    mutationFn: async (dispatchId: string) => {
      await axios.delete(`/api/master-data/dispatch/${dispatchId}?soft=false`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatches'] });
      toast({
        title: 'Dispatch Type deleted',
        description: 'Dispatch Type has been deleted successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to delete dispatch type',
        variant: 'destructive',
      });
    },
  });

  // Table columns
  const columns: TableColumn<Dispatch>[] = [
    {
      key: 'name',
      label: 'Dispatch Type Name',
      sortable: true,
    },
    {
      key: 'code',
      label: 'Code',
      sortable: true,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <Badge 
          variant={value === 'Active' ? 'default' : 'secondary'}
          className={value === 'Active' 
            ? 'bg-green-100 text-green-800 hover:bg-green-100' 
            : 'bg-red-100 text-red-800 hover:bg-red-100'
          }
        >
          {value}
        </Badge>
      ),
    },
  ];

  // Filters
  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'Filter by status',
      options: [
        { value: 'all', label: 'All Statuses' },
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ],
    },
  ];

  // Form sections with proper validation
  const formSections: FormSection[] = [
    {
      title: 'Dispatch Type Information',
      description: 'Basic dispatch type details',
      fields: [
        {
          name: 'name',
          label: 'Dispatch Type Name',
          type: 'text',
          required: true,
          placeholder: 'Enter unique dispatch type name',
          validation: z.string()
            .min(2, 'Dispatch type name must be at least 2 characters')
            .max(100, 'Dispatch type name cannot exceed 100 characters')
            .trim()
            .refine((val) => val.length > 0, 'Dispatch type name is required'),
        },
        {
          name: 'code',
          label: 'Code',
          type: 'text',
          required: true,
          placeholder: 'Enter unique code (uppercase letters, numbers, -, _)',
          validation: z.string()
            .min(2, 'Code must be at least 2 characters')
            .max(10, 'Code cannot exceed 10 characters')
            .trim()
            .toUpperCase()
            .regex(/^[A-Z0-9_-]+$/, 'Code can only contain uppercase letters, numbers, underscores and hyphens')
            .refine((val) => val.length > 0, 'Code is required'),
          description: 'Must be uppercase letters, numbers, underscores, or hyphens only',
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: statusOptions,
          validation: z.enum(['Active', 'Inactive'], {
            errorMap: () => ({ message: 'Please select a status' })
          }),
        },
      ],
    },
  ];

  const handleCreate = () => {
    setEditingDispatch(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (dispatch: Dispatch) => {
    setEditingDispatch(dispatch);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = (dispatch: Dispatch) => {
    deleteDispatchMutation.mutate(dispatch._id);
  };

  const handleSubmit = (data: any) => {
    // Transform data: ensure code is uppercase
    const transformedData = {
      ...data,
      code: data.code?.toUpperCase(),
    };

    if (editingDispatch) {
      updateDispatchMutation.mutate({ ...transformedData, _id: editingDispatch._id });
    } else {
      createDispatchMutation.mutate(transformedData);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingDispatch(null);
    setServerErrors({});
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['dispatches'] });
  };

  // Export functionality
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      
      // Add current filters to export (but not pagination - we want all records)
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`/api/master-data/dispatch/export?${params.toString()}`);
      
      if (response.data.success) {
        const data = response.data.data;
        
        // Convert to CSV
        if (data.length > 0) {
          const headers = ['Name', 'Code', 'Status', 'Created Date'];
          const rows = data.map((dispatch: any) => [
            dispatch.name || '',
            dispatch.code || '',
            dispatch.status || '',
            dispatch.createdAt ? new Date(dispatch.createdAt).toLocaleDateString() : ''
          ]);
          const csvContent = buildCsv(headers, rows, { quoteStrings: true });
          downloadText(
            csvContent,
            `dispatches_export_${new Date().toISOString().split('T')[0]}.csv`,
            'text/csv;charset=utf-8;'
          );
          
          toast({
            title: 'Export successful',
            description: `Exported ${data.length} dispatch type records.`,
          });
        } else {
          toast({
            title: 'No data to export',
            description: 'No dispatch types match the current filters.',
            variant: 'destructive',
          });
        }
      }
    } catch (error: any) {
      console.error('Export error:', error);
      toast({
        title: 'Export failed',
        description: error.response?.data?.error || 'Failed to export dispatch types',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dispatch Type Management</h1>
          <p className="text-muted-foreground">
            Manage dispatch type master data with comprehensive controls
          </p>
        </div>
      </div>

      <MasterDataTable<Dispatch>
        title="Dispatch Types"
        description="Manage and configure dispatch type information"
        data={dispatches}
        columns={columns}
        isLoading={isLoading}
        // Pagination
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Search
        currentSearch={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search dispatch types by name or code..."
        // Filtering
        currentFilters={currentFilters}
        onFiltersChange={setCurrentFilters}
        filters={filters}
        // Sorting
        currentSort={sortConfig}
        onSortChange={setSortConfig}
        // Actions
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        deleteConfirm
        // Permissions
        allowImportExport={true}
      />

      <MasterDataForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        title={editingDispatch ? 'Edit Dispatch Type' : 'Create Dispatch Type'}
        description={
          editingDispatch
            ? 'Update dispatch type information.'
            : 'Create a new dispatch type.'
        }
        sections={formSections}
        initialData={editingDispatch}
        onSubmit={handleSubmit}
        isLoading={createDispatchMutation.isPending || updateDispatchMutation.isPending}
        mode={editingDispatch ? 'edit' : 'create'}
        serverErrors={serverErrors}
        entityName="Dispatch Type"
      />
    </div>
  );
};

export default DispatchManagement;
